import { nanoid } from '@reduxjs/toolkit';
import type {
  AxisSeriesSettings,
  DefaultAxesParam,
  SplitSort,
} from '@/features/chartsView';
import {
  ChartType,
  DEFAULT_LEFT_AXIS_SERIES,
  DEFAULT_RIGHT_AXIS_SERIES,
  applyAxisMetric,
  applyAxisSeriesPatch,
  applyDefaultExcludedSplitOptions,
  applyExcludedSplitOptionsToggle,
  applySplitDimension,
  applySplitSortPatch,
  calendarChartDataConfigValidator,
  getRequestedMetrics,
  healAxesAgainstMeta,
  resetSplitSortToDefault,
} from '@/features/chartsView';
import type { Filters } from '@/features/filters';
import { prepareFilters } from '@/features/filters';
import { prepareDateRangeValueObject } from '@/shared/api';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import type { SerializedDateRangeValueObject } from '@/shared/types';
import { DateRangePresets, DateScale } from '@/shared/types';
import * as CalendarChartAPI from '../api/calendarChartAPI';
import type {
  CalendarChartConfig,
  CalendarDataResponse,
  ChartMeta,
} from './types';

export interface State {
  meta:
    | {
        data: ChartMeta;
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  chart: CalendarChartConfig | null;
  isConfigInitialized: boolean;
  splitSortBySession: Record<string, SplitSort>;
}

const initialState: State = {
  meta: {
    data: null,
    status: DataStatus.idle,
  },
  chart: null,
  isConfigInitialized: false,
  splitSortBySession: {},
};

export const calendarChartSlice = createAppSlice({
  name: 'calendarChart',
  initialState,
  reducers: (create) => ({
    initMeta: create.asyncThunk(
      (_: void) => {
        return CalendarChartAPI.getMeta();
      },
      {
        pending: (state) => {
          state.meta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.meta.data = action.payload;
          state.meta.status = DataStatus.finished;
          healAxesAgainstMeta(state);
        },
        rejected: (state) => {
          state.meta.status = DataStatus.error;
          state.meta.data = null;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;
            const { status } = state.calendarChart.meta;

            if (isLoading(status) || isFinished(status)) {
              return false;
            }
          },
        },
      },
    ),
    updateCohortDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.cohortDateScale = action.payload;
    }),
    updateCohortDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.cohortDateRange = action.payload;
      },
    ),
    updateRevenueDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.revenueDateScale = action.payload;
    }),
    updateRevenueDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.revenueDateRange = action.payload;
      },
    ),
    updateProbabilisticAttribution: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.probabilisticAttribution = action.payload;
    }),
    updateSplitDimension: create.reducer<string>((state, action) => {
      applySplitDimension(state, action.payload);
    }),
    updateSplitSort: create.reducer<Partial<SplitSort>>((state, action) => {
      applySplitSortPatch(state, action.payload);
    }),
    resetSplitSort: create.reducer((state) => {
      resetSplitSortToDefault(state);
    }),
    updatePrimaryMetric: create.reducer<string>((state, action) => {
      applyAxisMetric(state, 'primary', action.payload);
    }),
    updateSecondaryMetric: create.reducer<string>((state, action) => {
      applyAxisMetric(state, 'secondary', action.payload);
    }),
    updateAxisSeries: create.reducer<{
      axis: 'left' | 'right';
      patch: Partial<AxisSeriesSettings>;
    }>((state, action) => {
      applyAxisSeriesPatch(state, action.payload.axis, action.payload.patch);
    }),
    updateExcludedSplitOptions: create.reducer<string[]>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.excludedSplitOptions = action.payload;
    }),
    updateSyncedYAxes: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.syncedYAxes = action.payload;
    }),
    updateHighlightLineOnHover: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.highlightLineOnHover = action.payload;
    }),
    updateFilters: create.reducer<Filters>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.filters = action.payload;
    }),
    updateChart: create.reducer<CalendarChartConfig>((state, action) => {
      const parsed = calendarChartDataConfigValidator.safeParse(action.payload);

      state.chart = parsed.success ? parsed.data : action.payload;
      state.isConfigInitialized = true;
      healAxesAgainstMeta(state);
    }),
    resetChart: create.reducer((state) => {
      if (!isMetaFinished(state.meta)) {
        state.chart = null;

        return;
      }

      state.chart = {
        id: nanoid(),
        ...getDefaultChartProps(state.meta.data.defaultAxis),
      };
      state.isConfigInitialized = true;
      healAxesAgainstMeta(state);
    }),
    resetChartWithOverride: create.reducer<Partial<CalendarChartConfig>>(
      (state, action) => {
        if (!isMetaFinished(state.meta)) {
          state.chart = null;

          return;
        }

        state.chart = {
          id: nanoid(),
          ...getDefaultChartProps(state.meta.data.defaultAxis),
          ...action.payload,
        };
        state.isConfigInitialized = true;
        healAxesAgainstMeta(state);
      },
    ),
    setupDefaultExcludedSplitOptions: create.reducer<string[]>(
      (state, action) => {
        applyDefaultExcludedSplitOptions(state, action.payload);
      },
    ),
    toggleExcludedSplitOptions: create.reducer<{
      options: string[];
      checked: boolean;
    }>((state, action) => {
      applyExcludedSplitOptionsToggle(
        state,
        action.payload.options,
        action.payload.checked,
      );
    }),
  }),
  selectors: {
    selectChart: (state) => state.chart,
    selectIsConfigInitialized: (state) => state.isConfigInitialized,
    selectMeta: (state) => state.meta.data,
    selectMetaDataStatus: (state) => state.meta.status,
    selectMetaIsLoaded: (state) => isFinished(state.meta.status),
    selectMetaIsLoading: (state) => isLoading(state.meta.status),
  },
});

const getDefaultChartProps = (
  defaultAxes: DefaultAxesParam,
): Omit<CalendarChartConfig, 'id'> => {
  return {
    type: ChartType.calendar,
    excludedSplitOptions: [],
    syncedYAxes: true,
    highlightLineOnHover: true,
    filters: [],
    cohortDateScale: DateScale.DAY,
    cohortDateRange: { preset: DateRangePresets.ALL_TIME },
    revenueDateScale: DateScale.DAY,
    revenueDateRange: { preset: DateRangePresets.LAST_90_DAYS },
    probabilisticAttribution: true,
    splitDimension: defaultAxes.dimension,
    primaryMetric: defaultAxes.primaryMetric,
    secondaryMetric: defaultAxes.secondaryMetric,
    leftAxisSeries: { ...DEFAULT_LEFT_AXIS_SERIES },
    rightAxisSeries: { ...DEFAULT_RIGHT_AXIS_SERIES },
  };
};

function isMetaFinished(meta: State['meta']): meta is {
  data: ChartMeta;
  status: DataStatus.finished;
} {
  return meta.status === DataStatus.finished;
}

export const getChartData =
  (chart: CalendarChartConfig): AppThunk<Promise<CalendarDataResponse>> =>
  (_dispatch, getState) => {
    const state = getState() as RootState;

    if (!isFinished(state.calendarChart.meta.status)) {
      return Promise.reject(new Error('Meta not initialized'));
    }

    const { meta } = state.calendarChart;
    const { dimensionsFilters, metricsFilters } = prepareFilters(
      chart.filters,
      meta.data!.metrics,
      meta.data!.dimensions,
    );

    return CalendarChartAPI.getCalendarData({
      split: chart.splitDimension,
      metrics: getRequestedMetrics(chart),
      cohortDateScale: chart.cohortDateScale,
      cohortDateRange: prepareDateRangeValueObject(chart.cohortDateRange),
      revenueDateScale: chart.revenueDateScale,
      revenueDateRange: prepareDateRangeValueObject(chart.revenueDateRange),
      probabilisticAttribution: chart.probabilisticAttribution,
      dimensionsFilters,
      metricsFilters,
    });
  };

export const {
  initMeta,
  updateCohortDateScale,
  updateCohortDateRange,
  updateRevenueDateScale,
  updateRevenueDateRange,
  updateProbabilisticAttribution,
  updateSplitDimension,
  updateSplitSort,
  resetSplitSort,
  updatePrimaryMetric,
  updateSecondaryMetric,
  updateAxisSeries,
  updateExcludedSplitOptions,
  updateSyncedYAxes,
  updateHighlightLineOnHover,
  updateFilters,
  updateChart,
  resetChart,
  resetChartWithOverride,
  setupDefaultExcludedSplitOptions,
  toggleExcludedSplitOptions,
} = calendarChartSlice.actions;
export const {
  selectChart,
  selectIsConfigInitialized,
  selectMeta,
  selectMetaDataStatus,
  selectMetaIsLoaded,
  selectMetaIsLoading,
} = calendarChartSlice.selectors;
